// Tracks already-registered toggle elements to avoid duplicate listeners
const registeredToggles = new WeakSet<Element>();

export function initAccordions(): void {
    const toggles = document.querySelectorAll('[data-accordion-toggle]');

    toggles.forEach((toggle) => {
        // Skip already-registered toggles to prevent duplicate listeners
        if (registeredToggles.has(toggle)) {
            return;
        }

        registeredToggles.add(toggle);

        toggle.addEventListener('click', (e) => {
            e.preventDefault();
            const btn = (e.currentTarget as Element);
            const clickedKey = btn.getAttribute('data-accordion-toggle');

            if (clickedKey) {
                toggleAccordion(clickedKey);
            }
        });
    });
}

export function toggleAccordion(key: string): void {
    const content = document.querySelector(`[data-accordion-content="${key}"]`);
    const icon = document.querySelector(`[data-accordion-icon="${key}"]`);
    const toggle = document.querySelector(`[data-accordion-toggle="${key}"]`);

    if (!content) return;

    // Check if configuration overrides exist on the content element
    // This allows different accordions to have different transition classes
    const openClass = content.getAttribute('data-open-class') || 'max-h-[1000px] opacity-100';
    const closeClass = content.getAttribute('data-close-class') || 'max-h-0 opacity-0';
    const iconRotateClass = icon?.getAttribute('data-rotate-class') || 'rotate-180';

    const openClasses = openClass.split(' ');
    const closeClasses = closeClass.split(' ');

    // Determine state — check if all close classes are present
    const isClosed = closeClasses.every(c => content.classList.contains(c));

    if (isClosed) {
        // Open the accordion
        content.classList.remove(...closeClasses);
        content.classList.add(...openClasses);

        if (icon) {
            icon.classList.add(iconRotateClass);
        }

        toggle?.setAttribute('aria-expanded', 'true');
    } else {
        // Close the accordion
        content.classList.remove(...openClasses);
        content.classList.add(...closeClasses);

        if (icon) {
            icon.classList.remove(iconRotateClass);
        }

        toggle?.setAttribute('aria-expanded', 'false');
    }
}

/**
 * Watches the DOM for newly added accordion toggles and auto-initializes them.
 * This is useful when accordion HTML is rendered after the initial page load
 * (e.g. via AJAX, Razor partial views or tab switching).
 */
export function watchForNewAccordions(): void {
    const observer = new MutationObserver(() => {
        const newToggles = document.querySelectorAll('[data-accordion-toggle]');
        const unregistered = Array.from(newToggles).filter(t => !registeredToggles.has(t));

        if (unregistered.length > 0) {
            initAccordions();
        }
    });

    observer.observe(document.body, { childList: true, subtree: true });
}

// Auto-initialize when the DOM is ready if this script is included
// NOTE: main.ts also calls initAccordions() — this block only fires when accordion.ts
//       is used as a standalone entry point (not imported by another bundle).
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
        initAccordions();
        watchForNewAccordions();
    });
} else {
    initAccordions();
    watchForNewAccordions();
}
